import React from 'react';
import { Box, Radio, TextField, Typography } from '@mui/material';
import { Controller } from 'react-hook-form';

/**
 * One multiple choice answer, drawn as the same bordered box the quiz uses, with
 * the radio that marks it correct outside the box rather than in it — the box is
 * the answer, and the radio is what is being said about it.
 *
 * Props:
 *   id             (string)   - ID prefix for generated element IDs.
 *   control        (object)   - react-hook-form control for the editor's form.
 *   name           (string)   - Field path of the question this answer belongs to.
 *   questionNumber (number)   - Used for the accessible labels.
 *   index          (number)   - Zero-based position among the answers.
 *   optionKey      (string)   - The answer's letter, e.g. "A".
 *   isCorrect      (bool)     - Whether this is the marked correct answer.
 *   onMarkCorrect  (function) - Called when the radio is picked.
 */
export const AnswerRow = ({
  id,
  control,
  name,
  questionNumber,
  index,
  optionKey,
  isCorrect,
  onMarkCorrect,
}) => {
  const number = index + 1;

  return (
    <Box className="answerRow">
      <Radio
        id={`${id}__radio_answer_${number}`}
        checked={isCorrect}
        onChange={onMarkCorrect}
        size="small"
        slotProps={{
          input: { 'aria-label': `Mark question ${questionNumber} answer ${number} as correct` },
        }}
      />

      <Box className={`answerBody ${isCorrect ? 'correct' : ''}`}>
        <Typography className="answerKey" aria-hidden>
          {optionKey}.
        </Typography>

        <Controller
          name={`${name}.answers.${index}`}
          control={control}
          rules={{ validate: (value) => (value?.trim() ? true : 'Enter answer text.') }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              id={`${id}__input_answer_${number}`}
              className="answerInput"
              variant="standard"
              fullWidth
              multiline
              placeholder="Enter answer text"
              error={!!error}
              helperText={error?.message}
              slotProps={{
                input: { disableUnderline: true },
                htmlInput: { 'aria-label': `Question ${questionNumber} answer ${number}` },
              }}
            />
          )}
        />
      </Box>
    </Box>
  );
};
